import styled, { css } from 'styled-components';

import { MiniBadge } from '@xing-com/badge';
import { Card as BaseCard } from '@xing-com/card';
import { SGapCol } from '@xing-com/crate-jobs-components-columns';
import { OptimisticPublicationDate } from '@xing-com/crate-jobs-components-optimistic-publication-date';
import { lineClamp } from '@xing-com/crate-utils-line-clamp';
import { mediaConfined } from '@xing-com/layout-tokens';
import { Marker } from '@xing-com/marker';
import { ProfileImage } from '@xing-com/profile-image';
import { spaceL, spaceM, spaceS, spaceXS } from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

type IsCompactedProps = {
  $isCompacted: boolean;
};

export const Card = styled(BaseCard)<IsCompactedProps>`
  display: flex;
  height: 100%;

  ${({ $isCompacted }) => {
    const compactedStyles = css`
      max-height: 150px;
      min-height: 150px;
      max-width: 305px;
    `;

    return $isCompacted ? compactedStyles : '';
  }}

  @media ${mediaConfined} {
    ${({ $isCompacted }) => {
      const compactedStyles = css`
        max-height: 163px;
        min-height: 163px;
        max-width: 250px;
        width: 100%;
      `;

      return $isCompacted ? compactedStyles : '';
    }}
  }

  ${({ $isCompacted }) => {
    return $isCompacted
      ? `padding: ${spaceS} ${spaceL} ${spaceL} ${spaceL};`
      : '';
  }}

  & > div {
    height: 100%;
  }
`;

export const TopRow = styled.div<IsCompactedProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media ${mediaConfined} {
    ${({ $isCompacted }) => {
      return $isCompacted ? 'align-items: center;' : 'align-items: flex-start;';
    }}
  }
`;

export const Image = styled(ProfileImage)`
  pointer-events: none;
`;

export const PublicationDate = styled(OptimisticPublicationDate).attrs({
  lineHeight: 'dense',
  noMargin: true,
})`
  display: flex;
  align-items: center;

  @media ${mediaConfined} {
    margin-bottom: auto;
  }
`;

export const Notification = styled(MiniBadge)`
  margin-right: ${spaceXS};
`;

export const Details = styled(SGapCol)`
  flex-grow: 1;
`;

export const Title = styled(Headline)<IsCompactedProps>`
  ${({ $isCompacted }) => {
    return lineClamp($isCompacted ? 1 : 2);
  }}
  word-break: break-word;
`;

export const City = styled(BodyCopy)<IsCompactedProps>`
  ${({ $isCompacted }) => {
    return lineClamp($isCompacted ? 1 : 2);
  }}
  word-break: break-all;
`;

export const Company = styled(BodyCopy)<IsCompactedProps>`
  ${lineClamp(1)};
  word-break: break-all;

  @media ${mediaConfined} {
    ${({ $isCompacted }) => {
      return lineClamp($isCompacted ? 1 : 2);
    }}
    word-break: break-word;
  }
`;

export const HeaderContainer = styled.div<IsCompactedProps>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  ${({ $isCompacted }) => {
    return !$isCompacted ? `gap: ${spaceS};` : ``;
  }}

  @media ${mediaConfined} {
    ${({ $isCompacted }) => {
      return !$isCompacted ? `gap: ${spaceM};` : ``;
    }}
  }
`;

export const EndRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spaceS};
  justify-content: space-between;
  align-items: center;

  @media ${mediaConfined} {
    flex-direction: column;
    align-items: normal;
  }
`;

export const TopHighlight = styled(Marker)<{ $hasTopButton: boolean }>`
  ${({ $hasTopButton }) =>
    $hasTopButton &&
    css`
      margin-top: 10px;
      margin-right: 38px;
      align-self: flex-start;
    `}
`;
